import { useQuery } from '@apollo/client';
import { PermissionEnum, usePanel, ClinicDto } from '@ecdlink/core';
import debounce from 'lodash.debounce';
import { GetAllPortalClinics, GetSubDistrictsAndStats } from '@ecdlink/graphql';
import { SearchDropDown, SearchDropDownOption, Typography } from '@ecdlink/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ContentLoader } from '../../../../components/content-loader/content-loader';
import UiTable from '../../../../components/ui-table';
import { useUser } from '../../../../hooks/useUser';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  SearchIcon,
} from '@heroicons/react/solid';
import { CreateClinicPanel } from '../../components/create-clinic-panel/create-edit-clinic-panel';
import { useHistory } from 'react-router';
import { useUserRole } from '../../../../hooks/useUserRole';

export default function ClinicsSubPage() {
  const { hasPermission } = useUser();
  const { isAdministrator, isSuperAdmin } = useUserRole();
  const { data, refetch } = useQuery(GetAllPortalClinics, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: subDistrictsData } = useQuery(GetSubDistrictsAndStats, {
    fetchPolicy: 'cache-and-network',
  });

  const history = useHistory();

  const [searchValue, setSearchValue] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [subDistricts, setSubDistricts] = useState<
    SearchDropDownOption<string>[]
  >([]);

  const [tableData, setTableData] = useState<any[]>([]);

  const [subDistrictsFiltered, setSubDistrictsFiltered] =
    useState<SearchDropDownOption<string>[]>();

  const clearFilters = () => {
    setSubDistrictsFiltered([]);
  };

  const filteredSubDistricts = useMemo(
    () => subDistrictsFiltered?.map((item) => item?.id),
    [subDistrictsFiltered]
  );
  const numberOfFilters = useMemo(
    () => filteredSubDistricts?.length,
    [filteredSubDistricts?.length]
  );

  const renderFilterButtonText = useMemo(() => {
    if (numberOfFilters) {
      if (numberOfFilters === 1) {
        return `${numberOfFilters} Filter`;
      }
      return `${numberOfFilters} Filters`;
    }

    return 'Filter';
  }, [numberOfFilters]);

  const subDistrictsFilteredArray = useMemo(
    () =>
      data?.allPortalClinics?.filter((el) => {
        return filteredSubDistricts?.some((f) => {
          return f === el?.subDistrict?.id;
        });
      }),
    [data?.allPortalClinics, filteredSubDistricts]
  );

  useEffect(() => {
    if (data && data.allPortalClinics) {
      const copyItems = data.allPortalClinics
        .filter((v) => v?.isActive === true)
        .map((item: ClinicDto) => ({
          ...item,
          id: `${item?.id}`,
          name: item?.name,
          isActive: item?.isActive,
          _view: undefined,
          _edit: undefined,
          _url: undefined,
        }));
      setTableData(copyItems);
    }
  }, [data]);

  useEffect(() => {
    if (subDistrictsData?.subDistrictsAndStats?.length > 0) {
      setSubDistricts(
        subDistrictsData?.subDistrictsAndStats?.map((item) => {
          return {
            value: item?.id,
            label: item?.name,
            id: item?.id,
          };
        })
      );
    }
  }, [subDistrictsData?.subDistrictsAndStats]);

  useEffect(() => {
    if (subDistrictsFiltered?.length > 0) {
      const clinicsFilteredAndSorted = subDistrictsFilteredArray
        ?.slice()
        ?.sort((a, b) =>
          a.insertedDate > b.insertedDate
            ? -1
            : a.insertedDate < b.insertedDate
            ? 1
            : 0
        );
      setTableData(clinicsFilteredAndSorted);
    } else {
      const clinicsFilteredAndSorted = data?.allPortalClinics
        ?.slice()
        ?.sort((a, b) =>
          a.insertedDate > b.insertedDate
            ? -1
            : a.insertedDate < b.insertedDate
            ? 1
            : 0
        );
      setTableData(clinicsFilteredAndSorted);
    }
  }, [data, subDistrictsFiltered, subDistrictsFilteredArray]);

  const panel = usePanel();
  const displayPanel = () => {
    panel({
      noPadding: true,
      title: 'Add a clinic',
      render: (onSubmit: any) => (
        <CreateClinicPanel
          key={`clinicPanelCreate`}
          closeDialog={(clinicCreated: boolean) => {
            onSubmit();

            if (clinicCreated) {
              refetch();
            }
          }}
        />
      ),
    });
  };

  const filterByValue = useCallback((array, value) => {
    return array.filter(
      (data) =>
        JSON.stringify(data).toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
  }, []);

  const search = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value || '');
  }, 150);

  const handleViewClinic = useCallback(
    (clinic) => {
      history.push({
        pathname: '/clinics/view-clinics',
        state: {
          component: 'clinic',
          clinic: clinic,
        },
      });
    },
    [history]
  );

  if (tableData) {
    return (
      <div>
        <div className="flex flex-col">
          <div className="pb-5 sm:flex sm:items-center sm:justify-between">
            <div className="text-body w-8/12 sm:flex  sm:justify-around">
              <div className="text-body w-8/12 flex-col sm:flex sm:justify-around">
                <div className="relative w-full">
                  <span className="absolute inset-y-1/2 left-3 mr-4 flex -translate-y-1/2 transform items-center">
                    {searchValue === '' && (
                      <SearchIcon className="h-5 w-5 text-black"></SearchIcon>
                    )}
                  </span>
                  <input
                    className="focus:outline-none sm:text-md block w-full rounded-md bg-white py-3 pl-10 pr-3 leading-5 text-gray-900 placeholder-gray-600 focus:border-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-white"
                    placeholder="         Search by unique ID or clinic name..."
                    onChange={search}
                  />
                </div>
                {showFilter && (
                  <div className="mt-4 flex flex-row items-center gap-2 sm:mt-6">
                    <div className="relative w-6/12">
                      <SearchDropDown<string>
                        className={'w-full'}
                        menuItemClassName={
                          'absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-y-auto border border-gray-200 shadow-lg'
                        }
                        options={subDistricts}
                        selectedOptions={subDistrictsFiltered}
                        onChange={setSubDistrictsFiltered}
                        placeholder={'Sub-district'}
                        multiple={true}
                        color={'secondary'}
                        showSearch={true}
                      />
                    </div>

                    <button
                      onClick={clearFilters}
                      type="button"
                      className="text-secondary hover:bg-secondary outline-none inline-flex flex-shrink-0 items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium hover:text-white"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>

              <div className="mx-2 w-3/12">
                <span className="w-full text-lg font-medium leading-6 text-gray-900">
                  <button
                    onClick={() => setShowFilter(!showFilter)}
                    id="dropdownHoverButton"
                    className={`${
                      numberOfFilters
                        ? ' bg-secondary'
                        : 'border-secondary border-2 bg-white'
                    } focus:border-secondary focus:outline-none focus:ring-secondary dark:bg-secondary dark:hover:bg-grey-300 dark:focus:ring-secondary inline-flex items-center rounded-lg px-4 py-2.5 text-center text-sm font-medium ${
                      numberOfFilters ? 'text-white' : 'text-textMid'
                    } hover:bg-gray-300 focus:ring-2`}
                    type="button"
                  >
                    <div className="flex items-center gap-1">
                      <Typography
                        className="truncate"
                        type="help"
                        color={numberOfFilters ? 'white' : 'textLight'}
                        text={renderFilterButtonText}
                      />
                      {!showFilter ? (
                        <span>
                          <ChevronDownIcon
                            className={`h-6 w-6 ${
                              numberOfFilters ? 'text-white' : 'text-textLight'
                            }`}
                          />
                        </span>
                      ) : (
                        <span>
                          <ChevronUpIcon
                            className={`h-6 w-6 ${
                              numberOfFilters ? 'text-white' : 'text-textLight'
                            }`}
                          />
                        </span>
                      )}
                    </div>
                  </button>
                </span>
              </div>
            </div>
            <div className="mt-3 sm:mt-0 sm:ml-4">
              {hasPermission(PermissionEnum.create_user) &&
                (isAdministrator || isSuperAdmin) && (
                  <button
                    onClick={displayPanel}
                    type="button"
                    className="bg-secondary hover:bg-uiLight focus:outline-none inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:ring-2 focus:ring-offset-2"
                  >
                    + Add a Clinic
                  </button>
                )}
            </div>
          </div>

          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden border-b border-gray-200 shadow sm:rounded-lg">
                <UiTable
                  columns={[
                    { field: 'id', use: 'Unique ID' },
                    { field: 'name', use: 'Name' },
                    { field: 'teamLeads', use: 'teamLead(s)' },
                    { field: 'subDistrict', use: 'Sub-district' },
                    { field: 'insertedDate', use: 'Date added' },
                  ]}
                  rows={
                    searchValue !== 'Search by title or content...'
                      ? filterByValue(tableData, searchValue)
                      : tableData
                  }
                  viewRow={
                    hasPermission(PermissionEnum.update_user) &&
                    handleViewClinic
                  }
                  noBulkSelection={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return <ContentLoader />;
  }
}
